<div class="profile-settings-page">
  <h3 class="p-24 p-l-32 mat-h3">Edit your API access settings</h3>
  <div class="p-24 p-t-0 d-flex flex-col w-full">
  <!-- Chat Widget API Key Section -->
  <div class="api-key-section">
    <h3 class="mat-h3">Chat Widget API Key</h3>
    <p class="description">
      Generate an API key to embed the chat widget on external websites. The API key is
      domain-restricted for security.
    </p>

    @if (isLoadingApiKey) {
      <div class="m-b-16 description">Loading API key information...</div>
    }

    @if (!isLoadingApiKey && currentApiKey) {
      <div class="api-key-info m-b-16">
        <div class="d-flex align-items-center gap-8 m-b-8">
          <mat-icon class="text-success">check_circle</mat-icon>
          <span class="font-weight-medium">API Key Active</span>
        </div>
        <div class="info-row">
          <strong>Created:</strong> {{ currentApiKey.createdAt | date: 'medium' }}
        </div>
        <div class="info-row">
          <strong>Allowed Domains:</strong>
          @for (domain of currentApiKey.allowedDomains; track domain) {
            <span class="domain-chip">{{ domain }}</span>
          }
        </div>
        @if (currentApiKey.lastUsedAt) {
          <div class="info-row">
            <strong>Last Used:</strong> {{ currentApiKey.lastUsedAt | date: 'medium' }}
          </div>
        }
      </div>
    }

    @if (newlyGeneratedKey) {
      <div class="api-key-display m-b-16">
        <div class="d-flex align-items-center gap-8 m-b-8">
          <mat-icon class="text-warning">warning</mat-icon>
          <span class="font-weight-medium">Copy your API key now - it won't be shown again!</span>
        </div>
        <div class="api-key-value">
          <code>{{ newlyGeneratedKey }}</code>
          <button mat-icon-button matTooltip="Copy API Key" (click)="copyApiKey()">
            <mat-icon>content_copy</mat-icon>
          </button>
        </div>
        <div class="m-t-16">
          <div class="info-row m-b-8"><strong>Embed Code:</strong></div>
          <pre class="embed-code">{{ getEmbedCode() }}</pre>
          <button mat-stroked-button color="primary" (click)="copyEmbedCode()">
            <mat-icon>content_copy</mat-icon>
            Copy Embed Code
          </button>
        </div>
      </div>
    }

    <form class="form-field-full" [formGroup]="apiKeyForm">
      <h4 class="m-b-8 m-t-0">Allowed Domains</h4>
      <p class="description m-b-16">
        Enter comma-separated domains where the widget can be used. Use wildcards like
        <code>*.example.com</code> to allow all subdomains.
      </p>
      <div class="form-field-container">
        <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
          <input
            id="api-key-domains-input"
            matInput
            formControlName="allowedDomains"
            placeholder="example.com, *.staging.example.com, localhost"
          />
        </mat-form-field>
      </div>

      <div class="d-flex gap-8">
        <button
          mat-raised-button
          color="accent"
          [disabled]="isGeneratingKey || !apiKeyForm.valid"
          (click)="generateApiKey()"
        >
          @if (isGeneratingKey) {
            Generating...
          } @else if (currentApiKey) {
            Regenerate API Key
          } @else {
            Generate API Key
          }
        </button>

        @if (currentApiKey) {
          <button mat-stroked-button color="warn" (click)="revokeApiKey()">Revoke API Key</button>
        }
      </div>
    </form>
  </div>

  <hr class="m-t-32 m-b-32" />

  <h3 class="mat-h3">RISE settings</h3>
  <div class="m-b-24 m-t-8 suffix">
    To use RISE publications and clinical trials as your source data, you must provide credentials
    to RISE.
  </div>
  <form class="form-field-full" [formGroup]="editECRAccessForm">
    <h4 class="m-b-8 m-t-0">E-mail</h4>
    <div class="form-field-container">
      <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
        <input
          id="ecr-email-input"
          matInput
          type="text"
          formControlName="name"
          placeholder="Enter your email"
        />
      </mat-form-field>
    </div>

    <h4 class="m-b-8 m-t-0">Password</h4>
    <div class="form-field-container">
      <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
        <input
          id="ecr-password-input"
          matInput
          type="password"
          formControlName="password"
          placeholder="Enter your password"
        />
      </mat-form-field>
    </div>

    <button
      class="m-b-24"
      mat-raised-button
      color="accent"
      [disabled]="!editECRAccessForm.valid"
      (click)="saveECRAccessSettings()"
    >
      Save
    </button>
  </form>
  <h3 class="mat-h3">Confluence settings</h3>
  <div class="m-b-24 m-t-8 suffix">
    To import Confluence pages as your source data, you have to provide API settings.
  </div>
  <form class="form-field-full" [formGroup]="confluenceSettings">
    <h4 class="m-b-8 m-t-0">Username</h4>
    <div class="form-field-container">
      <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
        <input
          id="confluence-username-input"
          matInput
          formControlName="username"
          placeholder="Enter your Confluence username"
        />
      </mat-form-field>
    </div>
    <h4 class="m-b-8 m-t-0">API token</h4>
    <div class="m-b-24 m-t-8 suffix">
      To get API token go to Confluence Manage Account page then Security then Create and Manage API
      tokens
    </div>

    <div class="form-field-container">
      <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
        <input
          id="confluence-api-token-input"
          matInput
          type="password"
          formControlName="password"
          placeholder="Enter your API token"
        />
      </mat-form-field>
    </div>
    <h4 class="m-b-8 m-t-0">Url</h4>
    <div class="m-b-24 m-t-8 suffix">
      The URL for your Confluence instance including:
      <code>/wiki/rest/api</code>
    </div>
    <div class="form-field-container">
      <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
        <input
          id="confluence-url-input"
          matInput
          formControlName="url"
          placeholder="Enter your Confluence URL"
        />
      </mat-form-field>
    </div>

    <button
      class="m-b-24"
      mat-raised-button
      color="accent"
      [disabled]="!confluenceSettings.valid"
      (click)="saveConfluenceSettings()"
    >
      Save
    </button>
  </form>

  <h3 class="mat-h3">Veeva settings</h3>
  <div class="m-b-24 m-t-8 suffix">To import Veeva data, you have to provide API settings.</div>
  <form class="form-field-full" [formGroup]="veevaSettings">
    <h4 class="m-b-8 m-t-0">Username</h4>
    <div class="form-field-container">
      <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
        <input
          id="veeva-username-input"
          matInput
          formControlName="username"
          placeholder="Enter your Veeva username"
        />
      </mat-form-field>
    </div>
    <h4 class="m-b-8 m-t-0">Password</h4>

    <div class="form-field-container">
      <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
        <input
          id="veeva-password-input"
          matInput
          type="password"
          formControlName="password"
          placeholder="Enter your Veeva password"
        />
      </mat-form-field>
    </div>
    <h4 class="m-b-8 m-t-0">Url</h4>
    <div class="m-b-24 m-t-8 suffix">The URL for your Veeva instance</div>
    <div class="form-field-container">
      <mat-form-field class="gingr-input-wrapper w-full" appearance="outline">
        <input
          id="veeva-url-input"
          matInput
          formControlName="url"
          placeholder="Enter your Veeva URL"
        />
      </mat-form-field>
    </div>

    <button
      class="m-b-16"
      mat-raised-button
      color="accent"
      [disabled]="!veevaSettings.valid"
      (click)="saveVeevaSettings()"
    >
      Save
    </button>
  </form>

  <app-veeva-taxonomy-settings />
  </div>
</div>
